import React, { useState, useEffect, useCallback } from 'react';
import {
  BarChart, Bar, AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend, Line
} from 'recharts';
import {
  Users, Activity, Clock, TrendingUp, BarChart3, Eye,
  Loader2, RefreshCw, Briefcase, ClipboardList, UserCheck
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { tareasDespachoAPI } from '../api';

const COLORES = ['#3b82f6','#10b981','#f59e0b','#ef4444','#8b5cf6','#ec4899','#14b8a6','#f97316','#6366f1','#84cc16'];
const DIAS = ['Dom','Lun','Mar','Mie','Jue','Vie','Sab'];
const DIA_MAP = {1:0,2:1,3:2,4:3,5:4,6:5,7:6};
const MOD_LABELS = {abogados:'Abogados',minutas:'Minutas',costas:'Costas',ejecuciones:'Ejecuciones',pastelitos:'Pastelitos',citas:'Citas',intereses:'Intereses',cobrettis:'Cobrettis',honorarios:'Honorarios',clientes:'At. Cliente'};
const MOD_COLORS = {abogados:'#3b82f6',minutas:'#10b981',costas:'#f59e0b',ejecuciones:'#ef4444',pastelitos:'#8b5cf6',citas:'#ec4899',intereses:'#14b8a6',cobrettis:'#f97316',honorarios:'#6366f1',clientes:'#84cc16'};

function fmtFecha(d){if(!d)return '';return new Date(d).toLocaleDateString('es-ES',{day:'2-digit',month:'short'});}
function fmtHora(d){if(!d)return '';return new Date(d).toLocaleTimeString('es-ES',{hour:'2-digit',minute:'2-digit'});}

// Filtros compartidos
function Filtros({anno,setAnno,mes,setMes,dia,setDia,loading,onRefresh}) {
  const meses = [{v:null,l:'Todo el año'},{v:1,l:'Enero'},{v:2,l:'Febrero'},{v:3,l:'Marzo'},{v:4,l:'Abril'},{v:5,l:'Mayo'},{v:6,l:'Junio'},{v:7,l:'Julio'},{v:8,l:'Agosto'},{v:9,l:'Septiembre'},{v:10,l:'Octubre'},{v:11,l:'Noviembre'},{v:12,l:'Diciembre'}];
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <Select value={String(anno)} onValueChange={v=>{setAnno(Number(v));setDia(null);}}>
        <SelectTrigger className="w-24 h-8 text-xs"><SelectValue/></SelectTrigger>
        <SelectContent>{[2024,2025,2026].map(a=><SelectItem key={a} value={String(a)}>{a}</SelectItem>)}</SelectContent>
      </Select>
      <Select value={mes?String(mes):'all'} onValueChange={v=>{setMes(v==='all'?null:Number(v));setDia(null);}}>
        <SelectTrigger className="w-32 h-8 text-xs"><SelectValue/></SelectTrigger>
        <SelectContent>{meses.map(m=><SelectItem key={m.v||'all'} value={m.v?String(m.v):'all'}>{m.l}</SelectItem>)}</SelectContent>
      </Select>
      {mes&&(<Select value={dia?String(dia):'all'} onValueChange={v=>setDia(v==='all'?null:Number(v))}>
        <SelectTrigger className="w-20 h-8 text-xs"><SelectValue/></SelectTrigger>
        <SelectContent><SelectItem value="all">Todos</SelectItem>{Array.from({length:31},(_,i)=>i+1).map(d=><SelectItem key={d} value={String(d)}>{d}</SelectItem>)}</SelectContent>
      </Select>)}
      <Button variant="outline" size="sm" className="h-8" onClick={onRefresh} disabled={loading}>
        <RefreshCw className={`h-3.5 w-3.5 ${loading?'animate-spin':''}`}/>
      </Button>
    </div>
  );
}

export default function SupervisionPanel() {
  const hoy = new Date();
  const [anno,setAnno] = useState(hoy.getFullYear());
  const [mes,setMes] = useState(hoy.getMonth()+1);
  const [dia,setDia] = useState(null);
  const [tab,setTab] = useState('general');
  const [loading,setLoading] = useState(true);
  const [data,setData] = useState(null);
  const [tareasData,setTareasData] = useState(null);
  const [grafData,setGrafData] = useState(null);
  const [selectedUser,setSelectedUser] = useState(null);
  const [userDetail,setUserDetail] = useState(null);
  const [loadingDetail,setLoadingDetail] = useState(false);

  const params = useCallback(()=>{const p={anno};if(mes)p.mes=mes;if(dia)p.dia=dia;return p;},[anno,mes,dia]);

  const loadGeneral = useCallback(async()=>{
    setLoading(true);
    try{const r=await tareasDespachoAPI.getSupervisionResumen(params());setData(r.data);}
    catch(e){console.error(e);}
    finally{setLoading(false);}
  },[params]);

  const loadTareas = useCallback(async()=>{
    try{const r=await tareasDespachoAPI.getSupervisionTareasPorUsuario(params());setTareasData(r.data);}
    catch(e){console.error(e);}
  },[params]);

  const loadGraficos = useCallback(async()=>{
    try{const r=await tareasDespachoAPI.getSupervisionActividadGrafico(params());setGrafData(r.data);}
    catch(e){console.error(e);}
  },[params]);

  useEffect(()=>{loadGeneral();loadTareas();loadGraficos();},[loadGeneral,loadTareas,loadGraficos]);

  const loadUserDetail = async(uid)=>{
    setLoadingDetail(true);setSelectedUser(uid);
    try{const r=await tareasDespachoAPI.getSupervisionUsuarioDetalle(uid,params());setUserDetail(r.data);}
    catch(e){console.error(e);}
    finally{setLoadingDetail(false);}
  };

  const refresh=()=>{loadGeneral();loadTareas();loadGraficos();};

  if(loading&&!data) return <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-blue-600"/><span className="ml-3 text-gray-500">Cargando...</span></div>;

  const kpis = data?.kpis || {};
  const maxHp = data?.por_hora?.length>0?Math.max(1,...data.por_hora.map(h=>h.total)):1;

  return (
    <div className="space-y-4 p-4" data-testid="supervision-panel">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2"><Eye className="h-5 w-5 text-blue-600"/>Panel de Supervision</h2>
          <p className="text-sm text-gray-500">Periodo: {data?.periodo||''}</p>
        </div>
        <Filtros anno={anno} setAnno={setAnno} mes={mes} setMes={setMes} dia={dia} setDia={setDia} loading={loading} onRefresh={refresh}/>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <Card><CardContent className="p-3 text-center"><Activity className="h-5 w-5 mx-auto text-blue-500 mb-1"/><p className="text-2xl font-bold">{kpis.total_acciones||0}</p><p className="text-xs text-gray-500">Acciones</p></CardContent></Card>
        <Card><CardContent className="p-3 text-center"><Users className="h-5 w-5 mx-auto text-green-500 mb-1"/><p className="text-2xl font-bold">{kpis.usuarios_activos||0}</p><p className="text-xs text-gray-500">Usuarios activos</p></CardContent></Card>
        <Card><CardContent className="p-3 text-center"><Briefcase className="h-5 w-5 mx-auto text-purple-500 mb-1"/><p className="text-2xl font-bold">{kpis.tareas_completadas||0}</p><p className="text-xs text-gray-500">Tareas completadas</p></CardContent></Card>
        <Card><CardContent className="p-3 text-center"><TrendingUp className="h-5 w-5 mx-auto text-amber-500 mb-1"/><p className="text-2xl font-bold">{kpis.actuaciones_creadas||0}</p><p className="text-xs text-gray-500">Actuaciones</p></CardContent></Card>
        <Card><CardContent className="p-3 text-center"><BarChart3 className="h-5 w-5 mx-auto text-indigo-500 mb-1"/><p className="text-2xl font-bold">{kpis.expedientes_activos||0}</p><p className="text-xs text-gray-500">Expedientes</p></CardContent></Card>
        <Card><CardContent className="p-3 text-center"><Users className="h-5 w-5 mx-auto text-teal-500 mb-1"/><p className="text-2xl font-bold">{kpis.total_usuarios||0}</p><p className="text-xs text-gray-500">Total usuarios</p></CardContent></Card>
      </div>

      {/* Tabs */}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="general" className="text-xs"><Activity className="h-3.5 w-3.5 mr-1"/>General</TabsTrigger>
          <TabsTrigger value="tareas" className="text-xs"><ClipboardList className="h-3.5 w-3.5 mr-1"/>Tareas Despacho</TabsTrigger>
          <TabsTrigger value="usuarios" className="text-xs"><UserCheck className="h-3.5 w-3.5 mr-1"/>Actividad Usuarios</TabsTrigger>
        </TabsList>

        {/* ========== TAB GENERAL ========== */}
        <TabsContent value="general" className="space-y-4 mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Card className="lg:col-span-2">
              <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold flex items-center gap-2"><TrendingUp className="h-4 w-4 text-blue-600"/>Actividad diaria</CardTitle></CardHeader>
              <CardContent>{data?.evolucion_diaria?.length>0?(<ResponsiveContainer width="100%" height={200}><AreaChart data={data.evolucion_diaria}><defs><linearGradient id="gA" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/><stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/></linearGradient></defs><CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0"/><XAxis dataKey="fecha" tickFormatter={fmtFecha} tick={{fontSize:10}}/><YAxis tick={{fontSize:10}}/><Tooltip labelFormatter={fmtFecha}/><Area type="monotone" dataKey="acciones" stroke="#3b82f6" fill="url(#gA)" name="Acciones"/><Line type="monotone" dataKey="usuarios" stroke="#10b981" strokeWidth={2} name="Usuarios" dot={false}/></AreaChart></ResponsiveContainer>):<p className="text-center text-sm text-gray-400 py-8">Sin datos</p>}</CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold flex items-center gap-2"><BarChart3 className="h-4 w-4 text-purple-600"/>Uso por modulo</CardTitle></CardHeader>
              <CardContent>{data?.por_modulo?.length>0?(<div className="space-y-2 max-h-[200px] overflow-y-auto">{data.por_modulo.slice(0,12).map((m,i)=>{const mx=data.por_modulo[0]?.total||1;const pct=Math.round((m.total/mx)*100);return(<div key={m.modulo} className="flex items-center gap-2 text-xs"><span className="w-24 truncate text-gray-600 shrink-0">{m.modulo}</span><div className="flex-1 bg-gray-100 rounded-full h-4"><div className="h-4 rounded-full flex items-center justify-end pr-1" style={{width:`${pct}%`,backgroundColor:COLORES[i%COLORES.length]}}><span className="text-white text-[9px] font-bold">{m.total}</span></div></div></div>);})}</div>):<p className="text-center text-sm text-gray-400 py-8">Sin datos</p>}</CardContent>
            </Card>
          </div>
          {/* Heatmap */}
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold flex items-center gap-2"><Clock className="h-4 w-4 text-amber-600"/>Mapa de actividad (dia x hora)</CardTitle></CardHeader>
            <CardContent>
              <div className="text-[10px] text-gray-500 mb-1 flex items-center gap-0.5 pl-12">{Array.from({length:15},(_,i)=>i+7).map(h=><div key={h} className="flex-1 text-center">{h}h</div>)}</div>
              {DIAS.map((d,di)=>(<div key={di} className="flex items-center gap-0.5 mb-0.5"><div className="w-10 text-xs text-gray-500 text-right pr-2 font-medium">{d}</div>{Array.from({length:15},(_,i)=>i+7).map(h=>{const cell=data?.por_hora?.find(hp=>DIA_MAP[hp.dia_num]===di&&hp.hora===h);const val=cell?cell.total:0;const intensity=val>0?Math.min(0.15+(val/maxHp)*0.85,1):0;return(<div key={h} className="flex-1 aspect-square rounded-sm flex items-center justify-center text-[9px] font-medium" style={{backgroundColor:val>0?`rgba(59,130,246,${intensity})`:'#f3f4f6',color:intensity>0.5?'#fff':'#6b7280',minHeight:'24px'}} title={`${d} ${h}:00 - ${val} acciones`}>{val>0?val:''}</div>);})}</div>))}
            </CardContent>
          </Card>
          {/* Acciones recientes */}
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold flex items-center gap-2"><Activity className="h-4 w-4 text-indigo-600"/>Ultimas acciones</CardTitle></CardHeader>
            <CardContent>{data?.acciones_recientes?.length>0?(<div className="max-h-[250px] overflow-y-auto"><table className="w-full text-xs"><thead><tr className="border-b text-gray-500"><th className="text-left py-1.5">Fecha</th><th className="text-left">Usuario</th><th className="text-left">Accion</th><th className="text-left">Modulo</th></tr></thead><tbody>{data.acciones_recientes.map((a,i)=>(<tr key={i} className="border-b hover:bg-gray-50"><td className="py-1.5 text-gray-500">{a.fecha?`${fmtFecha(a.fecha)} ${fmtHora(a.fecha)}`:'-'}</td><td className="font-medium">{a.email}</td><td><Badge variant="outline" className="text-[10px]">{a.accion}</Badge></td><td className="text-gray-600">{a.recurso}</td></tr>))}</tbody></table></div>):<p className="text-center text-sm text-gray-400 py-6">Sin acciones</p>}</CardContent>
          </Card>
        </TabsContent>

        {/* ========== TAB TAREAS DESPACHO ========== */}
        <TabsContent value="tareas" className="space-y-4 mt-4">
          {tareasData?.usuarios?.length>0?(
            <div className="space-y-3">
              {tareasData.usuarios.map((u)=>{
                const totalCreadas=Object.values(u.modulos).reduce((s,m)=>s+m.creadas,0);
                const totalCompletadas=Object.values(u.modulos).reduce((s,m)=>s+m.completadas,0);
                const totalPendientes=Object.values(u.modulos).reduce((s,m)=>s+m.pendientes,0);
                const modEntries=Object.entries(u.modulos);
                const chartData=modEntries.map(([k,v])=>({modulo:MOD_LABELS[k]||v.label||k,creadas:v.creadas,completadas:v.completadas,pendientes:v.pendientes}));
                return (
                  <Card key={u.usuario_id}>
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-sm font-semibold">{u.nombre}</CardTitle>
                        <div className="flex gap-2 text-xs">
                          <Badge className="bg-blue-100 text-blue-700">{totalCreadas} creadas</Badge>
                          <Badge className="bg-green-100 text-green-700">{totalCompletadas} completadas</Badge>
                          <Badge className="bg-amber-100 text-amber-700">{totalPendientes} pendientes</Badge>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        {/* Chart */}
                        <ResponsiveContainer width="100%" height={Math.max(120,chartData.length*32)}>
                          <BarChart data={chartData} layout="vertical" margin={{left:10}}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0"/>
                            <XAxis type="number" tick={{fontSize:10}}/>
                            <YAxis type="category" dataKey="modulo" tick={{fontSize:10}} width={90}/>
                            <Tooltip/>
                            <Legend wrapperStyle={{fontSize:10}}/>
                            <Bar dataKey="completadas" fill="#10b981" name="Completadas" stackId="a" radius={[0,4,4,0]}/>
                            <Bar dataKey="pendientes" fill="#f59e0b" name="Pendientes" stackId="a" radius={[0,4,4,0]}/>
                          </BarChart>
                        </ResponsiveContainer>
                        {/* Table */}
                        <div className="overflow-x-auto">
                          <table className="w-full text-xs">
                            <thead><tr className="border-b text-gray-500"><th className="text-left py-1">Modulo</th><th className="text-right">Creadas</th><th className="text-right">Compl.</th><th className="text-right">Pend.</th><th className="text-right">%</th></tr></thead>
                            <tbody>
                              {modEntries.map(([k,v])=>(<tr key={k} className="border-b"><td className="py-1.5 font-medium" style={{color:MOD_COLORS[k]||'#333'}}>{MOD_LABELS[k]||v.label||k}</td><td className="text-right">{v.creadas}</td><td className="text-right text-green-600 font-semibold">{v.completadas}</td><td className="text-right text-amber-600">{v.pendientes}</td><td className="text-right font-semibold">{v.creadas>0?Math.round(v.completadas/v.creadas*100):0}%</td></tr>))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ):<p className="text-center text-sm text-gray-400 py-12">Sin datos de tareas para este periodo</p>}
        </TabsContent>

        {/* ========== TAB ACTIVIDAD USUARIOS ========== */}
        <TabsContent value="usuarios" className="space-y-4 mt-4">
          {/* Gráfico evolución diaria por usuario */}
          {grafData?.por_usuario_dia?.length>0&&(
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold flex items-center gap-2"><TrendingUp className="h-4 w-4 text-blue-600"/>Evolucion diaria por usuario</CardTitle></CardHeader>
              <CardContent>
                {(()=>{
                  const emails=new Set();
                  grafData.por_usuario_dia.forEach(d=>{Object.keys(d).filter(k=>k!=='fecha').forEach(k=>emails.add(k));});
                  const emailList=[...emails].slice(0,8);
                  return (
                    <ResponsiveContainer width="100%" height={250}>
                      <AreaChart data={grafData.por_usuario_dia}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0"/>
                        <XAxis dataKey="fecha" tickFormatter={fmtFecha} tick={{fontSize:10}}/>
                        <YAxis tick={{fontSize:10}}/>
                        <Tooltip labelFormatter={fmtFecha}/>
                        <Legend wrapperStyle={{fontSize:10}}/>
                        {emailList.map((email,i)=>(
                          <Area key={email} type="monotone" dataKey={email} stroke={COLORES[i%COLORES.length]} fill={COLORES[i%COLORES.length]} fillOpacity={0.1} strokeWidth={2} name={email.split('@')[0]} dot={false}/>
                        ))}
                      </AreaChart>
                    </ResponsiveContainer>
                  );
                })()}
              </CardContent>
            </Card>
          )}

          {/* Ranking + desglose por módulo por usuario */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Ranking */}
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold flex items-center gap-2"><UserCheck className="h-4 w-4 text-green-600"/>Ranking de actividad</CardTitle></CardHeader>
              <CardContent>
                {grafData?.ranking_acciones?.length>0?(
                  <div className="space-y-2">
                    {grafData.ranking_acciones.map((u,i)=>{
                      const mx=grafData.ranking_acciones[0]?.total||1;
                      const pct=Math.round((u.total/mx)*100);
                      return (
                        <div key={u.email} className="group">
                          <div className="flex items-center gap-2 text-xs cursor-pointer" onClick={()=>loadUserDetail(u.usuario_id)}>
                            <span className="w-5 font-bold text-gray-400">#{i+1}</span>
                            <span className="w-28 truncate font-medium text-gray-700 group-hover:text-blue-600">{u.email.split('@')[0]}</span>
                            <div className="flex-1 bg-gray-100 rounded-full h-5">
                              <div className="h-5 rounded-full flex items-center justify-between px-2" style={{width:`${pct}%`,backgroundColor:COLORES[i%COLORES.length]}}>
                                <span className="text-white text-[10px] font-bold">{u.total}</span>
                                <span className="text-white text-[9px]">{u.promedio_dia}/dia</span>
                              </div>
                            </div>
                            <span className="text-gray-400 text-[10px] w-14 text-right">{u.dias}d {u.modulos}m</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ):<p className="text-center text-sm text-gray-400 py-8">Sin datos</p>}
              </CardContent>
            </Card>

            {/* Desglose módulos por usuario */}
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold flex items-center gap-2"><BarChart3 className="h-4 w-4 text-purple-600"/>Modulos por usuario</CardTitle></CardHeader>
              <CardContent>
                {grafData?.por_usuario_modulo?.length>0?(
                  <div className="space-y-3 max-h-[350px] overflow-y-auto">
                    {grafData.por_usuario_modulo.map((u)=>(
                      <div key={u.email}>
                        <p className="text-xs font-semibold text-gray-700 mb-1">{u.email.split('@')[0]}</p>
                        <div className="flex flex-wrap gap-1">
                          {u.modulos.slice(0,8).map((m,i)=>(
                            <Badge key={m.modulo} style={{backgroundColor:`${COLORES[i%COLORES.length]}20`,color:COLORES[i%COLORES.length],borderColor:COLORES[i%COLORES.length]}} className="text-[10px] border">{m.modulo}: {m.total}</Badge>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ):<p className="text-center text-sm text-gray-400 py-8">Sin datos</p>}
              </CardContent>
            </Card>
          </div>

          {/* Detalle usuario seleccionado */}
          {selectedUser&&(
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-semibold flex items-center gap-2"><Eye className="h-4 w-4 text-blue-600"/>Detalle usuario</CardTitle>
                  <Button variant="ghost" size="sm" onClick={()=>{setSelectedUser(null);setUserDetail(null);}}>Cerrar</Button>
                </div>
              </CardHeader>
              <CardContent>{loadingDetail?(<div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin"/></div>):userDetail?(
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div>
                    <h4 className="text-xs font-semibold text-gray-600 mb-2">Acciones por modulo</h4>
                    {userDetail.acciones_por_modulo?.length>0?(<ResponsiveContainer width="100%" height={200}><BarChart data={userDetail.acciones_por_modulo.slice(0,10)} layout="vertical"><CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0"/><XAxis type="number" tick={{fontSize:10}}/><YAxis type="category" dataKey="modulo" tick={{fontSize:9}} width={80}/><Tooltip/><Bar dataKey="total" fill="#3b82f6" radius={[0,4,4,0]}/></BarChart></ResponsiveContainer>):<p className="text-xs text-gray-400">Sin datos</p>}
                  </div>
                  <div>
                    <h4 className="text-xs font-semibold text-gray-600 mb-2">Registro diario</h4>
                    <div className="max-h-[200px] overflow-y-auto space-y-1">
                      {userDetail.actividad_diaria?.map(d=>(<div key={d.fecha} className="flex items-center gap-2 text-xs border-b py-1.5"><span className="font-medium text-gray-700 w-20">{fmtFecha(d.fecha)}</span><Badge variant="outline" className="text-[10px]">{d.primera_accion?fmtHora(d.primera_accion):'-'} - {d.ultima_accion?fmtHora(d.ultima_accion):'-'}</Badge><span className="text-gray-500">{d.horas_actividad}h</span><span className="font-semibold text-blue-600 ml-auto">{d.acciones} acc.</span></div>))}
                    </div>
                  </div>
                </div>
              ):null}</CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
